import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["backdrop", "container", "dialog", "email", "messageContent"]
  static values = {
    open: { type: Boolean, default: false },
    participantId: String,
    participantName: String,
    participantEmail: String
  }

  connect() {
    this.handleKeyDown = this.handleKeyDown.bind(this)
    this.previousActiveElement = null

    // Show the modal wrapper (it's hidden by default)
    this.element.classList.remove("hidden")

    if (this.openValue) {
      this.show()
    }
  }

  disconnect() {
    document.removeEventListener("keydown", this.handleKeyDown)
    document.body.classList.remove("overflow-hidden")
  }

  openValueChanged() {
    if (this.openValue) {
      this.show()
    } else {
      this.hide()
    }
  }

  // Open modal with participant data
  openWithData(participant) {
    this.participantIdValue = participant.id
    this.participantNameValue = participant.name
    this.participantEmailValue = participant.email

    // Update the email display
    if (this.hasEmailTarget) {
      this.emailTarget.textContent = participant.email
    }

    // Update the message content with participant name
    if (this.hasMessageContentTarget) {
      const defaultMessage = `Hi ${participant.name.split(' ')[0]},

We noticed you haven't finished your "Finance Due Diligence Interview" yet.

Please complete it by November 12, 2025 to ensure your input is included.

You can pick up right where you left off:

[Continue Interview]

Thank you for your time and contribution.

— The inloop Team`
      this.messageContentTarget.value = defaultMessage
    }

    this.openValue = true
  }

  open() {
    this.openValue = true
  }

  close() {
    this.openValue = false
  }

  toggle() {
    this.openValue = !this.openValue
  }

  show() {
    // Store currently focused element to restore later
    this.previousActiveElement = document.activeElement

    // Show backdrop and container with animation
    this.backdropTarget.classList.remove("hidden")
    this.containerTarget.classList.remove("hidden")

    // Trigger reflow for animation
    void this.backdropTarget.offsetWidth

    // Add animation classes
    this.backdropTarget.classList.remove("opacity-0")
    this.backdropTarget.classList.add("opacity-100")

    this.dialogTarget.classList.remove("opacity-0", "scale-95")
    this.dialogTarget.classList.add("opacity-100", "scale-100")

    // Prevent body scroll
    document.body.classList.add("overflow-hidden")

    // Add keyboard listener
    document.addEventListener("keydown", this.handleKeyDown)

    // Focus first focusable element after animation
    requestAnimationFrame(() => {
      this.focusFirstElement()
    })
  }

  hide() {
    // Animate out
    this.backdropTarget.classList.remove("opacity-100")
    this.backdropTarget.classList.add("opacity-0")

    this.dialogTarget.classList.remove("opacity-100", "scale-100")
    this.dialogTarget.classList.add("opacity-0", "scale-95")

    // Wait for animation to complete before hiding
    setTimeout(() => {
      this.backdropTarget.classList.add("hidden")
      this.containerTarget.classList.add("hidden")
    }, 150)

    // Restore body scroll
    document.body.classList.remove("overflow-hidden")

    // Remove keyboard listener
    document.removeEventListener("keydown", this.handleKeyDown)

    // Return focus to previously focused element
    if (this.previousActiveElement && this.previousActiveElement.focus) {
      this.previousActiveElement.focus()
    }
  }

  handleKeyDown(event) {
    if (event.key === "Escape") {
      event.preventDefault()
      this.close()
    } else if (event.key === "Tab") {
      this.trapFocus(event)
    }
  }

  trapFocus(event) {
    const focusableElements = this.getFocusableElements()

    if (focusableElements.length === 0) return

    const firstElement = focusableElements[0]
    const lastElement = focusableElements[focusableElements.length - 1]

    if (event.shiftKey) {
      if (document.activeElement === firstElement) {
        event.preventDefault()
        lastElement.focus()
      }
    } else {
      if (document.activeElement === lastElement) {
        event.preventDefault()
        firstElement.focus()
      }
    }
  }

  getFocusableElements() {
    const selector = [
      'button:not([disabled])',
      'a[href]',
      'input:not([disabled])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      '[tabindex]:not([tabindex="-1"])'
    ].join(', ')

    return Array.from(this.dialogTarget.querySelectorAll(selector))
      .filter(el => el.offsetParent !== null)
  }

  focusFirstElement() {
    const focusableElements = this.getFocusableElements()

    if (focusableElements.length > 0) {
      const firstInteractive = focusableElements.find(el =>
        !el.getAttribute('aria-label')?.includes('Close')
      ) || focusableElements[0]

      firstInteractive.focus()
    }
  }

  stopPropagation(event) {
    event.stopPropagation()
  }

  // Send reminder action
  sendReminder() {
    const message = this.hasMessageContentTarget ? this.messageContentTarget.value : ""

    // In a real implementation, this would make an API call
    console.log('Sending reminder:', {
      participantId: this.participantIdValue,
      participantEmail: this.participantEmailValue,
      message: message
    })

    // Close the modal after sending
    this.close()

    // Show a success toast (if toast system is available)
    // this.dispatch("reminder-sent", { detail: { participantId: this.participantIdValue } })
  }
}
